<template>
  <footer class="footer">
    <div class="wrapper footer__wrapper">
      <div
        class="footer__col"
        v-if="column_1"
      >
        <app-logo
          :logo="column_1.logo"
          class="logo"
          v-if="translate(column_1.logo)"
        />

        <app-social-networks
          class="margin__t--48"
          :facebook="['fab', 'facebook-f']"
        />
      </div>

      <div
        class="footer__col"
        v-if="column_2"
      >
        <app-content :content="translate(column_2.text)" />
      </div>

      <div
        class="footer__col"
        v-if="column_3"
      >
        <nav>
          <ul class="menu">
            <template
              v-for="({ link }, index) in column_3.links"
              :key="`link_${index}`"
            >
              <li class="menu__li">
                <app-link
                  :link="link"
                  class="menu__a bold"
                />
              </li>
            </template>

            <li>
              <template v-if="isAuthenticated && user">
                <router-link
                  class="profile d-flex align-items-center profile bold"
                  :to="{ name: 'Profile', params: { locale } }"
                >
                  <user-profile-img
                    :avatar="user.avatar"
                    size="2"
                  />

                  <span class="margin__l--6">{{ user.firstname }}</span>
                </router-link>
              </template>

              <template v-else>
                <button
                  @click.prevent="openModal"
                  class="profile bold d-flex align-items-center"
                >
                  <fa-icon
                    :icon="['fas', 'user']"
                    fixed-width
                  />

                  <span class="margin__l--6">{{ t('literal.login') }}</span>
                </button>
              </template>
            </li>
          </ul>
        </nav>
      </div>

      <div
        class="footer__col"
        v-if="column_4"
      >
        <div class="donation padding--18">
          <h4
            class="donation__title bold"
            v-if="translate(column_4.donation.title)"
          >
            {{ translate(column_4.donation.title) }}
          </h4>

          <app-link
            :link="column_4.donation.cta"
            class="cta bold margin__t--18"
          />
        </div>
      </div>
    </div>

    <session-modal
      v-if="show_modal"
      @close="closeModal"
    />
  </footer>
</template>

<script setup>
import { defineAsyncComponent } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from '@/vendors/i18n'
import { useLanguageStore } from '@/stores/LanguageStore'
import { useAuthenticationStore } from '@/stores/AuthenticationStore'
import { useModals } from '@/composables/app/useModals'

import AppLogo from '&/atoms/AppLogo'
import AppSocialNetworks from '&/molecules/AppSocialNetworks'
const AppContent = defineAsyncComponent(() => import('&/atoms/AppContent'))
const AppLink = defineAsyncComponent(() => import('&/atoms/AppLink'))
const UserProfileImg = defineAsyncComponent(() => import('&/atoms/UserProfileImg'))
const SessionModal = defineAsyncComponent(() => import('&/organisms/SessionModal'))

const { section } = defineProps({
  section: { type: Object, required: true }
})

const { translate, t } = useI18n()
const { locale } = storeToRefs(useLanguageStore())
const { authUser: user, isAuthenticated } = storeToRefs(useAuthenticationStore())
const { show_modal, openModal, closeModal } = useModals()

const { column_1, column_2, column_3, column_4 } = section.attributes
</script>


<style lang="scss" scoped>
$color--primary: #131415;
$color--secondary: #FEFF00;
.wrapper {
  max-width: 1440px;
  padding: 0 $padding__base;
  margin: 0 auto;

  @include mq(sm) {
    padding: 0 $padding__base * 2;
  }

  @include mq(md) {
    padding: 0 $padding__base * 7;
  }
}

:deep(.cta) {
  padding: $padding__base $padding__base * 2;
  background: $color--primary;
  font-family: $font-family--secondary;
  color: $white;
  text-transform: uppercase;
  font-size: rem(16px);
  line-height: .9;
  transition: all $transition__duration;
  white-space: nowrap;
  display: inline-block;

  @include mq(sm) {
    padding: $padding__base * 1.5 $padding__base * 2.5;
    font-size: rem(20px);
  }

  &:hover {
    background: darken($color--primary, 10);
    color: $color--secondary;
  }
}

.footer {
  background: $color--secondary;
  padding: $padding__base * 4 0 $padding__base * 6;

  &__wrapper {
    display: grid;
    grid-template-columns: 1fr;
    gap: $margin__base * 2;

    @include mq(sm) {
      grid-template-columns: repeat(2, 1fr);
    }

    @include mq(lg) {
      grid-template-columns: repeat(4, 1fr);
    }
  }
}

:deep() {
  .networks {
    display: flex;
    align-items: center;

    &__li {
      width: auto;
      height: auto;

      &:not(:last-child) {
        margin-right: $margin__base * 1.4;
      }
    }

    &__a {
      display: block;
      width: 37px;
      height: 37px;
      line-height: 37px;
      text-align: center;
      border-radius: 50%;
      border: 1px solid $color--primary;
      transition: all .2s;

      &:hover {
        background: $color--primary;
        color: $color--secondary;
      }
    }
  }
}

.menu {
  &__li {
    &:not(:last-child) {
      margin-bottom: $margin__base;
    }
  }

  &:deep(.menu__a) {
    font-family: $font-family--secondary;
    font-size: rem(24px);
    line-height: 1.1;
    text-transform: uppercase;

    &.is-active, &:hover {
      text-decoration: underline;
    }
  }
}

.profile {
  font-family: $font-family--secondary;
  font-size: rem(24px);
  line-height: 1.1;
  text-transform: uppercase;
}

.donation {
  border: 1px solid $color--primary;

  &__title {
    font-family: $font-family--secondary;
    text-transform: uppercase;
    font-size: rem(17px);
    line-height: 1.1;
  }
}

:deep() {
  .logo {
    max-width: 100%;
    width: 192px;
    height: auto;
  }
}
</style>
